import { motion, AnimatePresence } from 'framer-motion'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { calculatePasswordStrength, getPasswordRequirements } from '../utils/passwordStrength'

interface PasswordStrengthBarProps {
  password: string
  showRequirements?: boolean
}

const PasswordStrengthBar = ({ password, showRequirements = true }: PasswordStrengthBarProps) => {
  const strength = calculatePasswordStrength(password)
  const requirements = getPasswordRequirements()

  if (!password) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="mt-4 space-y-3"
    >
      {/* Strength Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Password Strength</span>
          <motion.span
            key={strength.label}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="text-sm font-medium"
            style={{ color: strength.color }}
          >
            {strength.label}
          </motion.span>
        </div>
        
        <div className="relative h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
          <motion.div
            className={`h-full bg-gradient-to-r ${strength.gradient} rounded-full relative`}
            initial={{ width: 0 }}
            animate={{ width: `${(strength.score / 5) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {/* Animated shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 0.5 
              }}
            />
          </motion.div>
        </div>
      </div>

      {/* Requirements List */}
      <AnimatePresence>
        {showRequirements && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            <p className="text-xs text-gray-400 mb-3">Password must contain:</p>
            <div className="grid grid-cols-1 gap-2">
              {requirements.map((req, index) => {
                const isMet = strength.requirements[req.key as keyof typeof strength.requirements]
                return (
                  <motion.div
                    key={req.key}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center space-x-2 text-xs transition-colors duration-300 ${
                      isMet ? 'text-emerald-400' : 'text-gray-500'
                    }`}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.05 + 0.1 }}
                      className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isMet 
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                          : 'bg-white/10 border border-white/20'
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        {isMet ? (
                          <motion.div
                            key="check"
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            exit={{ scale: 0, rotate: 90 }}
                            transition={{ duration: 0.2 }}
                          >
                            <CheckIcon className="w-3 h-3 text-white" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="x"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <XMarkIcon className="w-3 h-3 text-gray-400" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    <motion.span
                      animate={{ 
                        color: isMet ? 'rgb(52, 211, 153)' : 'rgb(107, 114, 128)' 
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {req.label}
                    </motion.span>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default PasswordStrengthBar 